import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Container,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import EnrollmentWizard from './pages/EnrollmentWizard';
import { KioskAuthProvider } from './contexts/KioskAuthContext';
import { AccessibilityProvider, useAccessibility } from './contexts/AccessibilityContext';
import AccessibilityToolbar from './components/AccessibilityToolbar';
import { ProtectedRoute } from './components/ProtectedRoute';

// Theme creator with accessibility support
const createAccessibleTheme = (fontMultiplier: number, highContrast: boolean) => {
  const basePalette = {
    primary: {
      main: '#B8860B',
      light: '#DAA520',
      dark: '#8B6914',
    },
    secondary: {
      main: '#5D4037',
      light: '#8D6E63',
      dark: '#3E2723',
    },
    background: {
      default: '#FFF8E7',
      paper: '#FFFFFF',
    },
  };

  const highContrastPalette = {
    primary: {
      main: '#000000',
      light: '#333333',
      dark: '#000000',
    },
    secondary: {
      main: '#000000',
      light: '#333333',
      dark: '#000000',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#000000',
      secondary: '#000000',
    },
  };

  const palette = highContrast ? highContrastPalette : basePalette;

  return createTheme({
    palette,
    typography: {
      fontFamily: '"Roboto", "Noto Sans TC", "Noto Sans SC", sans-serif',
      // Scale all typography variants
      fontSize: 14 * fontMultiplier,
      h4: {
        fontWeight: 600,
        fontSize: `${2.125 * fontMultiplier}rem`,
      },
      h5: {
        fontWeight: 500,
        fontSize: `${1.5 * fontMultiplier}rem`,
      },
      h6: {
        fontSize: `${1.25 * fontMultiplier}rem`,
      },
      body1: {
        fontSize: `${1 * fontMultiplier}rem`,
      },
      body2: {
        fontSize: `${0.875 * fontMultiplier}rem`,
      },
      button: {
        fontSize: `${0.875 * fontMultiplier}rem`,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontSize: `${1.1 * fontMultiplier}rem`,
            padding: `${12 * fontMultiplier}px ${32 * fontMultiplier}px`,
            // Larger touch targets (min 48px for accessibility)
            minHeight: Math.max(48, 44 * fontMultiplier),
            minWidth: Math.max(48, 44 * fontMultiplier),
            ...(highContrast && {
              border: '2px solid #000000',
              '&:focus': {
                outline: '3px solid #000000',
                outlineOffset: 2,
              },
            }),
          },
          sizeLarge: {
            fontSize: `${1.2 * fontMultiplier}rem`,
            padding: `${14 * fontMultiplier}px ${40 * fontMultiplier}px`,
            minHeight: Math.max(56, 52 * fontMultiplier),
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            // Larger touch targets
            minWidth: Math.max(48, 44 * fontMultiplier),
            minHeight: Math.max(48, 44 * fontMultiplier),
            ...(highContrast && {
              '&:focus': {
                outline: '3px solid #000000',
                outlineOffset: 2,
              },
            }),
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-root': {
              fontSize: `${1.1 * fontMultiplier}rem`,
              minHeight: Math.max(48, 44 * fontMultiplier),
            },
            '& .MuiInputLabel-root': {
              fontSize: `${1 * fontMultiplier}rem`,
            },
            '& .MuiFormHelperText-root': {
              fontSize: `${0.85 * fontMultiplier}rem`,
            },
            ...(highContrast && {
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderWidth: 2,
                  borderColor: '#000000',
                },
                '&:hover fieldset': {
                  borderWidth: 2,
                  borderColor: '#000000',
                },
                '&.Mui-focused fieldset': {
                  borderWidth: 3,
                  borderColor: '#000000',
                },
              },
            }),
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            // Larger touch target
            padding: 12 * fontMultiplier,
            '& .MuiSvgIcon-root': {
              fontSize: `${1.5 * fontMultiplier}rem`,
            },
            ...(highContrast && {
              color: '#000000',
              '&.Mui-checked': {
                color: '#000000',
              },
            }),
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            padding: 12 * fontMultiplier,
            '& .MuiSvgIcon-root': {
              fontSize: `${1.5 * fontMultiplier}rem`,
            },
            ...(highContrast && {
              color: '#000000',
              '&.Mui-checked': {
                color: '#000000',
              },
            }),
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            // Ensure minimum touch target
            minHeight: Math.max(48, 44 * fontMultiplier),
          },
          label: {
            fontSize: `${1 * fontMultiplier}rem`,
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            height: 8 * fontMultiplier,
            ...(highContrast && {
              color: '#000000',
            }),
          },
          thumb: {
            width: Math.max(24, 20 * fontMultiplier),
            height: Math.max(24, 20 * fontMultiplier),
          },
          track: {
            height: 8 * fontMultiplier,
          },
          rail: {
            height: 8 * fontMultiplier,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontSize: `${0.9 * fontMultiplier}rem`,
            height: 'auto',
            padding: `${4 * fontMultiplier}px`,
            ...(highContrast && {
              borderWidth: 2,
              borderColor: '#000000',
            }),
          },
          label: {
            padding: `${4 * fontMultiplier}px ${8 * fontMultiplier}px`,
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            fontSize: `${0.75 * fontMultiplier}rem`,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: `${1 * fontMultiplier}rem`,
            minHeight: Math.max(48, 44 * fontMultiplier),
            ...(highContrast && {
              '&:focus': {
                backgroundColor: '#000000',
                color: '#FFFFFF',
              },
            }),
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            minHeight: Math.max(48, 44 * fontMultiplier),
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            ...(highContrast && {
              border: '2px solid #000000',
              '&:before': {
                display: 'none',
              },
            }),
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            minHeight: Math.max(56, 48 * fontMultiplier),
          },
          content: {
            '& .MuiTypography-root': {
              fontSize: `${1 * fontMultiplier}rem`,
            },
          },
        },
      },
    },
  });
};

// Inner app component that uses accessibility context
function AppContent() {
  const { t, i18n } = useTranslation();
  const { highContrast, fontSizeMultiplier } = useAccessibility();
  const [languageAnchor, setLanguageAnchor] = useState<null | HTMLElement>(null);

  const languages = [
    { code: 'zh-TW', label: '繁體中文' },
    { code: 'en', label: 'English' },
    { code: 'zh-CN', label: '简体中文' },
  ];

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageAnchor(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageAnchor(null);
  };

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    handleLanguageClose();
  };

  const currentLanguage = useMemo(() => {
    return languages.find(l => l.code === i18n.language)?.label || '繁體中文';
  }, [i18n.language]);

  // Create theme based on accessibility settings
  const theme = useMemo(
    () => createAccessibleTheme(fontSizeMultiplier, highContrast),
    [fontSizeMultiplier, highContrast]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <KioskAuthProvider>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
          }}
        >
          <AppBar
            position="static"
            color="primary"
            elevation={0}
            sx={{
              ...(highContrast && {
                bgcolor: '#000000',
                color: '#FFFFFF',
              }),
            }}
          >
            <Toolbar
              sx={{
                gap: 2,
                flexWrap: 'wrap',
                py: 1,
                minHeight: Math.max(64, 56 * fontSizeMultiplier),
              }}
            >
              <Typography
                variant="h5"
                component="div"
                sx={{
                  flexGrow: 1,
                  fontWeight: 600,
                  fontSize: `${1.25 * fontSizeMultiplier}rem`,
                }}
              >
                {t('app.title')}
              </Typography>

              {/* Accessibility Toolbar */}
              <AccessibilityToolbar />

              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                  mx: 1,
                }}
              />

              {/* Language Switcher */}
              <IconButton
                color="inherit"
                onClick={handleLanguageClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: Math.max(48, 44 * fontSizeMultiplier),
                  minHeight: Math.max(48, 44 * fontSizeMultiplier),
                }}
                aria-label={t('button.changeLanguage', 'Change Language')}
              >
                <LanguageIcon sx={{ fontSize: `${1.5 * fontSizeMultiplier}rem` }} />
                <Typography
                  variant="body2"
                  sx={{ fontSize: `${0.9 * fontSizeMultiplier}rem` }}
                >
                  {currentLanguage}
                </Typography>
              </IconButton>
              <Menu
                anchorEl={languageAnchor}
                open={Boolean(languageAnchor)}
                onClose={handleLanguageClose}
              >
                {languages.map((lang) => (
                  <MenuItem
                    key={lang.code}
                    selected={i18n.language === lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                  >
                    {lang.label}
                  </MenuItem>
                ))}
              </Menu>
            </Toolbar>
          </AppBar>

          <ProtectedRoute>
            <Container
              maxWidth="md"
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                py: 3,
              }}
            >
              <EnrollmentWizard />
            </Container>
          </ProtectedRoute>

          <Box
            component="footer"
            sx={{
              py: 2,
              textAlign: 'center',
              bgcolor: highContrast ? '#000000' : 'primary.dark',
              color: 'white',
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: `${0.875 * fontSizeMultiplier}rem` }}
            >
              © {new Date().getFullYear()} {t('app.title')}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.5,
                opacity: 0.6,
                fontSize: `${0.7 * fontSizeMultiplier}rem`,
              }}
            >
              v{__APP_VERSION__} ({import.meta.env.VITE_BUILD_TAG || 'dev'})
            </Typography>
          </Box>
        </Box>
      </KioskAuthProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AccessibilityProvider>
      <AppContent />
    </AccessibilityProvider>
  );
}

export default App;
